import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Cpu, Info } from "lucide-react";

import { api, ApiError, type CapacityLimits } from "../../api";
import { InfoHint } from "../../components/InfoHint";
import { Note, SettingsSection } from "./shared";

/**
 * AI-camera limits: visible to everyone, editable by dev users only.
 *
 * Everyone hits these limits when adding a camera, so hiding them would make
 * the refusal look arbitrary. Changing them is a different matter: the ceiling
 * is bounded by what the detection engine was built to batch and by the GPU's
 * decode headroom, so it is a deployment decision rather than an operator one.
 */
export function CapacitySection() {
  const qc = useQueryClient();
  const limits = useQuery({ queryKey: ["limits"], queryFn: api.limits.get });
  const [fleet, setFleet] = useState<string>("");
  const [perStore, setPerStore] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!limits.data) return;
    setFleet(String(limits.data.max_ai_cameras_configured));
    setPerStore(String(limits.data.max_ai_cameras_per_store));
  }, [limits.data]);

  const save = useMutation({
    mutationFn: (body: { max_ai_cameras: number; max_ai_cameras_per_store: number }) =>
      api.limits.update(body),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      qc.invalidateQueries({ queryKey: ["limits"] });
      qc.invalidateQueries({ queryKey: ["camera-capacity"] });
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : "Could not save the limits."),
  });

  if (limits.isLoading) {
    return (
      <SettingsSection title="Capacity & limits">
        <div className="bf-card text-sm text-bf-muted">Loading limits…</div>
      </SettingsSection>
    );
  }

  const data = limits.data as CapacityLimits;
  const editable = data.editable;
  const clamped =
    data.engine_ai_ceiling != null &&
    data.max_ai_cameras_configured > data.engine_ai_ceiling;

  const perStoreRows = Object.entries(data.ai_per_store).sort((a, b) =>
    a[0].localeCompare(b[0]),
  );

  return (
    <SettingsSection
      title="Capacity & limits"
      description="How many cameras may be analysed by the AI."
      hint={
        <>
          Only AI cameras are limited: they occupy a slot in the detection
          engine, which is sized when the pipeline starts. Cameras relayed for
          live viewing are unlimited. Limits are enforced when a camera is
          added, and decide which cameras are analysed if a system is already
          over them.
        </>
      }
    >
      {error && <Note tone="error">{error}</Note>}
      {saved && !save.isPending && <Note tone="success">Limits saved.</Note>}

      <div className="grid gap-3 lg:grid-cols-2">
        <div className="bf-card space-y-3">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-white">
            <Cpu size={15} /> AI camera limits
          </h3>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs text-bf-muted">
                Across all stores
              </span>
              <input
                className="bf-input disabled:opacity-60"
                type="number"
                min={1}
                max={512}
                value={fleet}
                disabled={!editable}
                onChange={(e) => {
                  setFleet(e.target.value);
                  setSaved(false);
                }}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-bf-muted">Per store</span>
              <input
                className="bf-input disabled:opacity-60"
                type="number"
                min={1}
                max={512}
                value={perStore}
                disabled={!editable}
                onChange={(e) => {
                  setPerStore(e.target.value);
                  setSaved(false);
                }}
              />
            </label>
          </div>

          {clamped && (
            <Note tone="warning">
              The detection engine was built to analyse{" "}
              {data.engine_ai_ceiling} cameras at once, so the effective limit is{" "}
              {data.max_ai_cameras} regardless of the {data.max_ai_cameras_configured}{" "}
              set here. Raising it further needs the engine rebuilt, which takes
              minutes and happens on the next restart.
            </Note>
          )}

          {editable ? (
            <div className="flex justify-end">
              <button
                className="bf-button disabled:opacity-40"
                disabled={save.isPending}
                onClick={() =>
                  save.mutate({
                    max_ai_cameras: Number(fleet),
                    max_ai_cameras_per_store: Number(perStore),
                  })
                }
              >
                {save.isPending ? "Saving…" : "Save limits"}
              </button>
            </div>
          ) : (
            <p className="flex items-center gap-1.5 text-xs text-bf-muted">
              <Info size={13} className="shrink-0" />
              Set by a developer account — shown here so you can see what is left
              <InfoHint>
                Raising these is a deployment decision: the ceiling is bounded by
                what the detection engine was built to batch and by the GPU's
                decode headroom, so only a developer account can change them.
              </InfoHint>
            </p>
          )}
        </div>

        <div className="bf-card space-y-3">
          <h3 className="text-sm font-semibold text-white">In use now</h3>
          <div className="space-y-2">
            <UsageRow
              label="AI cameras, all stores"
              used={data.ai_used}
              max={data.max_ai_cameras}
            />
            {perStoreRows.map(([store, used]) => (
              <UsageRow
                key={store}
                label={store || "No store"}
                used={used}
                max={data.max_ai_cameras_per_store}
                sub
              />
            ))}
            <div className="flex items-center gap-1.5 border-t border-white/5 pt-2 text-xs text-bf-muted">
              {data.view_only_used} non-AI camera
              {data.view_only_used === 1 ? "" : "s"}, not limited
              <InfoHint>
                Non-AI cameras are relayed for live viewing only. They use no
                detection slot and no GPU at all until somebody opens one, so
                there is no ceiling on how many you add.
              </InfoHint>
            </div>
          </div>
        </div>
      </div>
    </SettingsSection>
  );
}

function UsageRow({
  label,
  used,
  max,
  sub = false,
}: {
  label: string;
  used: number;
  max: number;
  sub?: boolean;
}) {
  const pct = max > 0 ? Math.min(100, (used / max) * 100) : 0;
  const full = used >= max;
  return (
    <div className={sub ? "pl-3" : ""}>
      <div className="flex items-center justify-between gap-3 text-xs">
        <span className={sub ? "text-bf-muted" : "text-bf-text"}>{label}</span>
        <span className={full ? "text-amber-300" : "text-bf-muted"}>
          {used}/{max}
        </span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-white/5">
        <div
          className={
            "h-full rounded-full " + (full ? "bg-amber-400" : "bg-bf-gradient")
          }
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
